"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorPickerFieldLogic = void 0;
const differenceWith_1 = __importDefault(require("lodash-es/differenceWith"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const fields_1 = require("../../utils/fields");
const migration_1 = require("../../utils/migration");
const color_picker_fieldtype_1 = require("../../safe-field-value/text-fields/color-picker-field/color-picker.fieldtype");
const validation_1 = require("../../validation/validation");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const comparisons_1 = require("../../comparisons/comparisons");
const clipboard_1 = require("../../clipboard/clipboard");
const bulk_update_1 = require("../../utils/bulk-update");
class ColorPickerFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor(config) {
        super(config);
        this.name = "colorpickerfield";
        this.icon = "drop";
        this.iconColor = "#883CD0";
        this.label = (0, intl_1.$t)(texts_1.colorPickerFieldText.color_picker);
        this.placeholder = (0, intl_1.$t)(texts_1.colorPickerFieldText.enter_color_code);
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isColor,
            is_not: comparisons_1.notIsColor,
            is_empty: comparisons_1.isEmptyColor,
            is_not_empty: comparisons_1.isNotEmptyColor,
            contains: comparisons_1.containsColor,
            not_contains: comparisons_1.notContainsColor,
        };
        this.clipboardPasteRules = {
            colorpickerfield: comparisons_1.clipboardColorToSelf,
            textfield: clipboard_1.clipboardTextToColor,
            recordtitlefield: clipboard_1.clipboardTextToColor,
            textareafield: clipboard_1.clipboardTextToColor,
            richtextareafield: clipboard_1.clipboardTextToColor,
            unknownfield: clipboard_1.clipboardTextToColor,
        };
        this.fieldValueGetter = new color_picker_fieldtype_1.ColorPickerFieldValueGetter();
        this.defaultMaxLength = config.default_max_length;
    }
    get validators() {
        return { required: validation_1.required, colorField: validation_1.colorFieldValidator };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            saveKeys: types_1.navigationKeys,
            rollbackOnPressEscapeKey: false,
            editOnStartTyping: false,
            switchToInputControl: "OnCellSelect",
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            editorWidth: "100%",
            inputControlEditMode: "OnShow",
            cellPresenceEventMode: "OnModalOpen",
            needUseDefaultValue: (value, isDirty) => {
                const isNeedUseDefaultValue = (0, fields_1.needUseDefaultFieldValue)(field, value);
                return isDirty && isNeedUseDefaultValue;
            },
            spaceKeyOpensRecordModal: () => true,
        };
    }
    valueToString(value, { params }) {
        if (!value) {
            return "";
        }
        if (typeof value === "string") {
            return value;
        }
        if (!Array.isArray(value)) {
            return "";
        }
        // Returns color name and value (considering the field display format)
        return value
            .map((val) => {
            return (0, comparisons_1.colorValueToString)(val, params.display_format);
        })
            .join(" | ");
    }
    sortKey(value, { params }) {
        if (!Array.isArray(value)) {
            return "";
        }
        // Sort by color value, considering the field display format
        const displayFormat = params.display_format;
        return value
            .map((color) => (0, comparisons_1.parseColor)(color.value))
            .filter((color) => !!color)
            .map((color) => (0, comparisons_1.colorToString)(color, displayFormat))
            .join(", ");
    }
    groupByKey(value) {
        if (!Array.isArray(value)) {
            return "";
        }
        // Group by unique set of hex values
        return value
            .map((val) => (0, comparisons_1.parseColor)(val.value))
            .filter((color) => !!color)
            .map(comparisons_1.colorToHex)
            .sort()
            .join(", ");
    }
    getEmptyValue() {
        return [];
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return { previousValue, currentValue };
        }
        const removedValues = (0, differenceWith_1.default)(previousValue, currentValue, react_fast_compare_1.default);
        const addedValues = (0, differenceWith_1.default)(currentValue, previousValue, react_fast_compare_1.default);
        return { previousValue: removedValues, currentValue: addedValues };
    }
    replaceValue(field, record, sourceValue = [], targetValue = []) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 140,
            minWidth: 70,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.colorPickerDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    filterToRecordValue(filterValue, comparison, field) {
        if (comparison !== "is" || !filterValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const values = filterValue.split("|");
        const colors = values
            .map((v) => (0, comparisons_1.parseColor)(v.trim()))
            .filter((c) => !!c)
            .map(comparisons_1.colorToHex)
            .map((hex) => ({ value: hex, name: "" }))
            .sort((a, b) => a.value.localeCompare(b.value));
        if (values.length !== colors.length) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        return colors;
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        if (field.params.entries_allowed === "single") {
            return super.applyBulkUpdate(field, value, update);
        }
        return (0, bulk_update_1.applyBulkUpdateToDictsArray)(value, update, "value");
    }
}
exports.ColorPickerFieldLogic = ColorPickerFieldLogic;
